import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const CART_KEY = 'cg_cart_v1';
const CartContext = createContext({
  items: [],
  add: () => {},
  remove: () => {},
  setQty: () => {},
  setNote: () => {},
  clear: () => {},
  count: 0,
  subtotal: 0,
});

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(CART_KEY) || '[]');
      setItems(Array.isArray(stored) ? stored : []);
    } catch (e) {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
  }, [items]);

  const add = useCallback((menuItem, quantity = 1, note = '') => {
    setItems((prev) => {
      const existing = prev.find((i) => i.menu_item_id === menuItem.id);
      if (existing) {
        return prev.map((i) =>
          i.menu_item_id === menuItem.id ? { ...i, quantity: i.quantity + quantity, special_instructions: note || i.special_instructions } : i,
        );
      }
      return [
        ...prev,
        {
          menu_item_id: menuItem.id,
          name: menuItem.name,
          price: menuItem.price,
          image_url: menuItem.image_url,
          quantity,
          special_instructions: note,
        },
      ];
    });
  }, []);

  const remove = useCallback((menu_item_id) => {
    setItems((prev) => prev.filter((i) => i.menu_item_id !== menu_item_id));
  }, []);

  const setQty = useCallback((menu_item_id, quantity) => {
    setItems((prev) =>
      prev
        .map((i) => (i.menu_item_id === menu_item_id ? { ...i, quantity: Math.max(0, quantity) } : i))
        .filter((i) => i.quantity > 0),
    );
  }, []);

  const setNote = useCallback((menu_item_id, note) => {
    setItems((prev) => prev.map((i) => (i.menu_item_id === menu_item_id ? { ...i, special_instructions: note } : i)));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = items.reduce((s, i) => s + i.quantity, 0);
  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, add, remove, setQty, setNote, clear, count, subtotal }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
